/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Components
import {
    Box,
    Button,
    Container,
    Divider,
    Flex,
    Heading,
    Stack,
    useMultiStyleConfig,
    useTheme,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import LinksList from './links-list'

// Others
import {categoryUrlBuilder} from '../../utills/url'
// import Link from '@salesforce/retail-react-app/app/components/link'
import {pxToRem} from '../../utills/pxToRem'
import {ChevronLeftIcon, CloseIcon} from '../icons'
import {fonts} from '../../theme/foundations/fonts'

const ListMenuContentInner = ({
    item,
    onClose,
    items = [],
    initialFocusRef,
    selectedLevelOneMenu = {},
    setfeatureImage,
    setLinksForLevelThreeMenu,
    setLinksForLevelTwoMenu,
    setIsLevelThreeMenuOpen,
    setIsLevelTwoMenuOpen,
    isLevelTwoMenuOpen,
    isLevelThreeMenuOpen,
    isLevelTwo,
    isMobileMenu = false,
    setFeatureImageMobileLevelTwo,
    setFeatureImageMobileLevelThree,
    setFeatureImageMobileLevelFour,
    setMenuToggle,
    setSelectedLevelOneMenu,
    setSelectedLevelTwoMenu,
    selectedLevelTwoMenu
}) => {
    const theme = useTheme()
    const {baseStyle} = theme.components.ListMenu
    const {locale} = useIntl()
    const intl = useIntl()
    const variant = 'vertical'

    const styles = useMultiStyleConfig('LinksList', {variant})

    const [linksForContentComp, setLinksForContentComp] = useState([])

    useEffect(() => {
        if (
            !isMobileMenu &&
            (isLevelTwoMenuOpen || isLevelThreeMenuOpen) &&
            initialFocusRef?.current
        ) {
            initialFocusRef.current.focus()
        }
    }, [isLevelTwoMenuOpen, isLevelThreeMenuOpen, isMobileMenu, initialFocusRef])

    const handleCloseLevelThreeMenu = () => {
        setIsLevelThreeMenuOpen(false)
        setLinksForLevelThreeMenu([])
        setSelectedLevelTwoMenu({})
    }

    const handleCloseLevelTwoMenu = () => {
        setIsLevelTwoMenuOpen(false)
        setIsLevelThreeMenuOpen(false)
        setLinksForLevelTwoMenu([])
        setLinksForLevelThreeMenu([])
        setSelectedLevelOneMenu({})
        setSelectedLevelTwoMenu({})
    }

    const updateGTMdataLayer = (isLevel2, level1Text, level2Text) => {
        if (window?.dataLayer) {
            window.dataLayer.push({
                event: 'mainNavClick',
                valueL1: isLevel2 ? level1Text : level2Text,
                valueL2: `view all ${isLevel2 ? level1Text : level2Text}`
            })
        }
    }

    return (
        <Container
            as={Stack}
            {...baseStyle.popoverContainer}
            {...baseStyle.overflow}
            position={'relative'}
            marginTop={{base: pxToRem(50), sm: pxToRem(50), md: 0, lg: 0}}
        >
            <Flex
                direction="column"
                flex={'1'}
                borderRight={
                    (isLevelTwoMenuOpen || isLevelThreeMenuOpen) && !isMobileMenu
                        ? `${pxToRem(1)} solid black`
                        : '0 solid black'
                }
            >
                {/* close menu button */}
                {isMobileMenu && (isLevelTwoMenuOpen || isLevelThreeMenuOpen) && (
                    <>
                        <Box {...styles.back}>
                            <ChevronLeftIcon
                                fontFamily={fonts.primary}
                                fontWeight={'700'}
                                marginLeft={pxToRem(-10)}
                            />
                            <Button
                                onClick={
                                    isLevelTwo ? handleCloseLevelTwoMenu : handleCloseLevelThreeMenu
                                }
                                _hover={{
                                    textDecoration: 'none',
                                    color: 'black'
                                }}
                                _active={{
                                    backgroundColor: 'transparent'
                                }}
                                backgroundColor={'transparent'}
                                height={'auto'}
                                paddingTop={pxToRem(10)}
                                paddingLeft={'0'}
                                paddingBottom={pxToRem(10)}
                                color={'black'}
                                display={'block'}
                                width={'10px'}
                                fontSize={pxToRem(14)}
                                fontFamily={fonts.primary}
                                fontWeight={'700'}
                                justifyContent={'flex-end'}
                                letterSpacing={pxToRem(1)}
                            >
                                {intl.formatMessage({
                                    id: 'header.link.back',
                                    defaultMessage: 'BACK'
                                })}
                            </Button>
                        </Box>
                        {/* <Divider {...styles.backDivider} /> */}
                    </>
                )}

                {!isMobileMenu && (isLevelTwoMenuOpen || isLevelThreeMenuOpen) && (
                    <Button
                        ref={initialFocusRef}
                        onClick={isLevelTwo ? handleCloseLevelTwoMenu : handleCloseLevelThreeMenu}
                        aria-label={
                            isLevelTwo
                                ? `Close ${selectedLevelOneMenu?.text}`
                                : `Close ${selectedLevelTwoMenu?.text}`
                        }
                        _hover={{
                            textDecoration: 'none',
                            color: 'white'
                        }}
                        _active={{
                            backgroundColor: 'transparent'
                        }}
                        backgroundColor={'transparent'}
                        height={'auto'}
                        padding={'0'}
                        color={'black'}
                        display={'block'}
                        width={'10px'}
                        fontSize={pxToRem(14)}
                        fontFamily={fonts.primary}
                        fontWeight={700}
                        justifyContent={'flex-end'}
                        position={'absolute'}
                        right={pxToRem(0)}
                    >
                        X
                    </Button>
                )}

                {(isLevelTwoMenuOpen || isLevelThreeMenuOpen) && (
                    <>
                        <Link
                            href={
                                isLevelTwo
                                    ? categoryUrlBuilder(selectedLevelOneMenu, locale)
                                    : categoryUrlBuilder(selectedLevelTwoMenu, locale)
                            }
                            onClick={() =>
                                updateGTMdataLayer(
                                    isLevelTwo,
                                    selectedLevelOneMenu?.text,
                                    selectedLevelTwoMenu?.text
                                )
                            }
                            {...styles.headingLink}
                            _hover={{
                                textDecoration: 'none'
                            }}
                            paddingTop={{
                                base: pxToRem(10),
                                sm: pxToRem(10),
                                md: pxToRem(0),
                                lg: pxToRem(0)
                            }}
                            paddingBottom={{
                                base: pxToRem(10),
                                sm: pxToRem(10),
                                md: pxToRem(0),
                                lg: pxToRem(0)
                            }}
                            paddingLeft={'0'}
                            paddingRight={{
                                base: pxToRem(18),
                                sm: pxToRem(18),
                                md: pxToRem(15),
                                lg: pxToRem(15)
                            }}
                        >
                            <Heading
                                {...styles.heading}
                                fontSize={[pxToRem(13), pxToRem(13), pxToRem(13), pxToRem(13)]}
                                fontWeight="400"
                                padding={[0, 0, 0, 0]}
                            >
                                {isLevelTwo
                                    ? `view all ${selectedLevelOneMenu?.text}`
                                    : `view all ${selectedLevelTwoMenu?.text}`}
                            </Heading>
                        </Link>
                    </>
                )}

                {items.map((menuItem) => {
                    const {id, name, c_headerMenuBanner} = menuItem

                    const heading = {
                        id: id,
                        image: c_headerMenuBanner,
                        href: categoryUrlBuilder(menuItem, locale),
                        text: name
                    }

                    const links = items
                        ? items.map((item) => {
                              const {name, id, categories} = item
                              return {
                                  id: id,
                                  categories: categories,
                                  name: name,
                                  href: categoryUrlBuilder(item, locale),
                                  text: name,
                                  styles: {
                                      fontSize: 'md',
                                      paddingTop: 3,
                                      paddingBottom: 3
                                  }
                              }
                          })
                        : []

                    return (
                        <>
                            <LinksList
                                key={id}
                                onLinkClick={onClose}
                                isInnerMenu={true}
                                heading={heading}
                                setSelectedLevelOneMenu={setSelectedLevelOneMenu}
                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                selectedLevelOneMenu={selectedLevelOneMenu}
                                setSelectedLevelTwoMenu={setSelectedLevelTwoMenu}
                                setMenuToggle={setMenuToggle}
                                hasSubMenu={menuItem.categories != null}
                                isMobileMenu={isMobileMenu}
                                links={links}
                                setfeatureImage={setfeatureImage}
                                color={'gray.900'}
                                setLinksForLevelThreeMenu={setLinksForLevelThreeMenu}
                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                setLinksForContentComp={setLinksForContentComp}
                                setFeatureImageMobileLevelTwo={setFeatureImageMobileLevelTwo}
                                setFeatureImageMobileLevelThree={setFeatureImageMobileLevelThree}
                                setFeatureImageMobileLevelFour={setFeatureImageMobileLevelFour}
                                parentCategoryName={
                                    isLevelTwo
                                        ? selectedLevelOneMenu?.text
                                        : selectedLevelTwoMenu?.text
                                }
                            />
                        </>
                    )
                })}
            </Flex>
        </Container>
    )
}

ListMenuContentInner.propTypes = {
    item: PropTypes.object,
    items: PropTypes.array,
    maxColumns: PropTypes.number,
    setSelectedLevelOneMenu: PropTypes.func,
    setSelectedLevelTwoMenu: PropTypes.func,
    onClose: PropTypes.func,
    initialFocusRef: PropTypes.object,
    selectedLevelOneMenu: PropTypes.object,
    selectedLevelTwoMenu: PropTypes.object,
    setLinksForLevelThreeMenu: PropTypes.func,
    setLinksForLevelTwoMenu: PropTypes.func,
    setIsLevelThreeMenuOpen: PropTypes.func,
    setIsLevelTwoMenuOpen: PropTypes.func,
    isLevelTwoMenuOpen: PropTypes.oneOf[(true, false)],
    isLevelThreeMenuOpen: PropTypes.oneOf[(true, false)],
    isLevelTwo: PropTypes.oneOf[(true, false)],
    isMobileMenu: PropTypes.oneOf[(true, false)],
    setFeatureImageMobileLevelTwo: PropTypes.func,
    setFeatureImageMobileLevelThree: PropTypes.func,
    setFeatureImageMobileLevelFour: PropTypes.func,

    setMenuToggle: PropTypes.func,
    setfeatureImage: PropTypes.func
}

export {ListMenuContentInner}
